/**
 * Активные навыки героев (E3) — реализация по `SkillDef.kind`, параметры из `SkillDef.params`
 * (`docs/context/content-v1.md`). Навык не срабатывает (возвращает `false`, перезарядка не идёт), если ему не по
 * кому бить — так «Автонавыки» не тратят навык на пустое поле.
 *
 * Сила навыка (`skillPower`, звёзды и усиления) увеличивает урон (`airstrike`, `chainLightning`, `firewall`, `freeze`) и длительность —
 * `barrage` (дольше стреляет) и `freeze` (дольше стоят). Урон навыков растёт и с уровнем героя, как обычная атака.
 */
import type { SkillDef, SkillKind, Vec2 } from "@newfungame/contracts";
import { PERMILLE, TICK_RATE } from "@newfungame/contracts";
import { dist2, divTrunc, mulPermille } from "../sim/math";
import type { HeroEntity, SimApi, ZombieEntity } from "../sim/types";
import { skillDamage, type SkillStats } from "./skill-stats";
import { freezeZombie, markBurning, markShocked, slowZombie } from "./status";
import { bestBy, crowdAround, inRange } from "./targeting";

export interface SkillContext {
  hero: HeroEntity;
  sim: SimApi;
  skill: SkillDef;
  stats: SkillStats;
}

/** Как навык меняет обычную стрельбу, пока действует. */
export interface ShootingOverride {
  /** Снарядов за выстрел сверх обычных. */
  extraShots: number;
  /** Перезарядка выстрела, тиков. */
  cooldownTicks: number;
}

export interface SkillHandler {
  /** Длительность действия с учётом силы (ядро кладёт её в `activeLeft` при срабатывании). */
  duration(skill: SkillDef, stats: SkillStats): number;
  /** Применить навык; `false` — не сработал (некуда бить). */
  cast(ctx: SkillContext): boolean;
  /** Каждый тик боя (после `activeLeft--` ядра), до стрельбы героя. */
  tick?(ctx: SkillContext): void;
  /** Изменение стрельбы, пока навык действует. */
  shooting?(ctx: SkillContext): ShootingOverride | null;
}

const param = (skill: SkillDef, name: string, fallback: number): number => skill.params[name] ?? fallback;

/** Шквал (Gunner): `durationTicks` × сила стреляет в `fireRatePermille` чаще, по `projectileCount` пули. */
export const barrage: SkillHandler = {
  duration: (skill, st) => mulPermille(skill.durationTicks, st.power),
  cast({ hero, sim }) {
    if (!sim.findTarget(hero.x, hero.y, hero.attack.range)) return false;
    hero.cooldownLeft = 0; // первый выстрел шквала — сразу
    return true;
  },
  shooting({ hero, skill }) {
    if (hero.skill.activeLeft <= 0) return null;
    const rate = Math.max(PERMILLE, param(skill, "fireRatePermille", 3000));
    return {
      extraShots: Math.max(0, param(skill, "projectileCount", 2) - 1),
      cooldownTicks: Math.max(1, divTrunc(hero.attack.cooldownTicks * PERMILLE, rate)),
    };
  },
};

/** Центры бомб: жадно — каждая следующая туда, где больше всего ещё не накрытых; накрыты все — в самую густую. */
export function airstrikeCenters(zombies: readonly ZombieEntity[], radius: number, bombs: number): ZombieEntity[] {
  const r2 = radius * radius;
  const covered = new Set<number>();
  const out: ZombieEntity[] = [];
  for (let b = 0; b < bombs && zombies.length > 0; b++) {
    let best: ZombieEntity | null = null;
    let bestNew = -1;
    let bestAll = -1;
    for (const z of zombies) {
      let fresh = 0;
      let all = 0;
      for (const q of zombies) {
        if (dist2(z.x, z.y, q.x, q.y) > r2) continue;
        all++;
        if (!covered.has(q.id)) fresh++;
      }
      if (fresh > bestNew || (fresh === bestNew && all > bestAll)) {
        best = z;
        bestNew = fresh;
        bestAll = all;
      }
    }
    out.push(best!);
    for (const q of zombies) if (dist2(best!.x, best!.y, q.x, q.y) <= r2) covered.add(q.id);
  }
  return out;
}

/** Авиаудар (Bomber): `bombs` бомб по `damage` в радиусе `radius` по самой густой толпе на всём поле. */
export const airstrike: SkillHandler = {
  duration: (skill) => skill.durationTicks,
  cast({ sim, skill, stats }) {
    const zs = sim.zombies();
    if (zs.length === 0) return false;
    const radius = param(skill, "radius", 2500);
    const dmg = skillDamage(param(skill, "damage", 120), stats);
    for (const c of airstrikeCenters(zs, radius, param(skill, "bombs", 5))) {
      const at = { x: c.x, y: c.y };
      for (const z of sim.zombiesInRadius(at.x, at.y, radius)) sim.damage(z, dmg, false);
      sim.effect("explosion", at, null, 20, radius);
    }
    return true;
  },
};

/** Цепная молния (Sparky): `damage` по цели героя и дальше прыжками на `jumpRange` по `targets` целям, −15 % за прыжок. */
export const chainLightning: SkillHandler = {
  duration: (skill) => skill.durationTicks,
  cast({ hero, sim, skill, stats }) {
    let cur: ZombieEntity | null = sim.findTarget(hero.x, hero.y, hero.attack.range);
    if (!cur) return false;
    const targets = param(skill, "targets", 6);
    const jump = param(skill, "jumpRange", 3000);
    const falloff = param(skill, "falloffPermille", 850);
    let dmg = skillDamage(param(skill, "damage", 80), stats);
    let from = { x: hero.x, y: hero.y };
    const hit = new Set<number>();
    for (let i = 0; i < targets && cur; i++) {
      const at: Vec2 = { x: cur.x, y: cur.y };
      hit.add(cur.id);
      sim.effect("lightning", from, at, 10, i);
      sim.damage(cur, Math.max(1, dmg), false);
      if (cur.hp > 0) markShocked(cur, sim.tick, divTrunc(TICK_RATE, 2));
      from = at;
      dmg = mulPermille(dmg, falloff);
      const r2 = jump * jump;
      const next: ZombieEntity[] = sim.zombiesInRadius(at.x, at.y, jump).filter((z) => !hit.has(z.id));
      cur = null;
      let bd = 0;
      for (const z of next) {
        const d = dist2(at.x, at.y, z.x, z.y);
        if (d <= r2 && (cur === null || d < bd || (d === bd && z.id < cur.id))) {
          cur = z;
          bd = d;
        }
      }
    }
    return true;
  },
};

/** Пауза между ударами стены огня, тиков (5 раз в секунду). */
export const FIREWALL_PULSE_TICKS = 6;

/**
 * Стена огня (Blaze): на `durationTicks` полоса `width` × `depth` поперёк поля на `distance` от штаба — с той
 * стороны (к +y или −y), где больше зомби; жжёт `damagePerSecond` в секунду всех, кто внутри.
 */
export const firewall: SkillHandler = {
  duration: (skill) => skill.durationTicks,
  cast({ hero, sim, skill }) {
    const zs = sim.zombies();
    if (zs.length === 0) return false;
    let north = 0;
    for (const z of zs) north += z.y >= sim.hq.y ? 1 : -1;
    const dist = param(skill, "distance", 5000);
    const maxY = sim.hq.y * 2; // штаб в центре поля
    const y = Math.max(0, Math.min(maxY, sim.hq.y + (north >= 0 ? dist : -dist)));
    const half = divTrunc(param(skill, "width", 7000), 2);
    hero.data.fireY = y;
    hero.data.fireLeft = hero.skill.durationTicks;
    sim.effect(
      "fire",
      { x: sim.hq.x - half, y },
      { x: sim.hq.x + half, y },
      hero.skill.durationTicks,
      param(skill, "depth", 1200),
    );
    return true;
  },
  tick({ hero, sim, skill, stats }) {
    const left = hero.data.fireLeft;
    if (left === undefined) return;
    const next = left - 1;
    if (next % FIREWALL_PULSE_TICKS === 0) {
      const y = hero.data.fireY!;
      const halfW = divTrunc(param(skill, "width", 7000), 2);
      const halfD = divTrunc(param(skill, "depth", 1200), 2);
      const dps = skillDamage(param(skill, "damagePerSecond", 40), stats);
      const dmg = Math.max(1, divTrunc(dps * FIREWALL_PULSE_TICKS, TICK_RATE));
      for (const z of sim.zombies()) {
        if (Math.abs(z.x - sim.hq.x) > halfW || Math.abs(z.y - y) > halfD) continue;
        sim.damage(z, dmg, false);
        if (z.hp > 0) markBurning(z, sim.tick, FIREWALL_PULSE_TICKS + 1);
      }
    }
    if (next <= 0) {
      delete hero.data.fireLeft;
      delete hero.data.fireY;
    } else hero.data.fireLeft = next;
  },
};

/**
 * Заморозка (Frosty): `damage` и стоп (`slowPermille` 1000) на `durationTicks` × сила всем в круге `radius` у самой
 * густой толпы в радиусе героя, затем `afterSlowTicks` замедления на `afterSlowPermille`.
 */
export const freeze: SkillHandler = {
  duration: (skill, st) => mulPermille(skill.durationTicks, st.power),
  cast({ hero, sim, skill, stats }) {
    const list = inRange(hero, sim);
    if (list.length === 0) return false;
    const radius = param(skill, "radius", 4000);
    const all = sim.zombies();
    const center = bestBy(list, sim, (z) => crowdAround(z, all, radius))!;
    const at = { x: center.x, y: center.y };
    const dmg = skillDamage(param(skill, "damage", 30), stats);
    const ticks = hero.skill.durationTicks;
    const after = param(skill, "afterSlowTicks", 60);
    for (const z of sim.zombiesInRadius(at.x, at.y, radius)) {
      sim.damage(z, dmg, false);
      if (z.hp <= 0) continue;
      freezeZombie(z, sim.tick, ticks, param(skill, "slowPermille", PERMILLE));
      slowZombie(z, sim.tick, ticks + after, param(skill, "afterSlowPermille", 500));
    }
    sim.effect("ice", at, null, ticks, radius);
    return true;
  },
};

export const SKILL_HANDLERS: Record<SkillKind, SkillHandler> = {
  barrage,
  airstrike,
  chainLightning,
  firewall,
  freeze,
};
